'use client';

import { BlockRoot, PortalLink, tx } from '@akademi/site-builder-runtime';
import { TradeStyles } from '../styles/TradeStyles';
import { HOME_CSS } from '../styles/homeCss';
import { SHOP_INTRO_CSS } from '../styles/shopIntroCss';
import { DawnScape, TileGlyph } from '../home/sceneArt';
import { useLiveLines, useAddLines } from '../home/useLiveProducts';
import { LivePrice, AddButton } from '../home/parts';
import { useSolarText } from '../locales/useSolarText';

type Localized = string | Record<string, string>;
type Journey = { kicker: Localized; title: Localized; text: Localized; label: Localized; href: string };
type Chip = { label: Localized; href: string };

export interface SolarShopIntroProps {
  eyebrow?: Localized;
  title?: Localized;
  text?: Localized;
  shopLabel?: Localized;
  shopHref?: string;
  designLabel?: Localized;
  designHref?: string;
  /** The old drawn-art caption. Kept so published pages still validate; the Horizon hero draws no caption. */
  illustrationLabel?: Localized;
  /** Category links under the actions. Labels and routes only — no counts, because the block has no live count to show. */
  chips?: Chip[];
  /** The glass spotlight: one real product by its catalogue slug, read live. Absent or unresolvable, no card renders. */
  featuredSlug?: string;
  /** The small pill on the spotlight card, e.g. "Featured". */
  featuredTag?: Localized;
  journeys?: Journey[];
}

/**
 * The Horizon hero (redesign 2026-10-01): the dawn-scape behind an on-dark first screen —
 * eyebrow, display title, sub, one amber shopping action with a quiet planner action beside
 * it, category chips — and the glass spotlight carrying one live, priced product. The two
 * journey cards below follow the mockups' path-card anatomy on the selling surface.
 */
export function SolarShopIntro({
  eyebrow, title, text, shopLabel, shopHref = '/categories',
  designLabel, designHref = '/design', chips = [], featuredSlug, featuredTag, journeys = [],
}: SolarShopIntroProps) {
  const t = useSolarText();
  if (!tx(title)) return null;
  const shownChips = chips.filter((chip) => chip.href && tx(chip.label));
  const shownJourneys = journeys.filter((journey) => journey.href && tx(journey.title));
  return (
    <BlockRoot as="section" className="ae ae-intro">
      <TradeStyles id="home" css={HOME_CSS} />
      <TradeStyles id="shop-intro" css={SHOP_INTRO_CSS} />
      <div className="ae-hero">
        <DawnScape />
        <div className="ae-wrap ae-hero__grid">
          <div className="ae-hero__copy">
            {tx(eyebrow) ? <p className="ae-hero__eyebrow">{tx(eyebrow)}</p> : null}
            <h1 className="ae-hero__title">{tx(title)}</h1>
            {tx(text) ? <p className="ae-hero__sub">{tx(text)}</p> : null}
            <div className="ae-hero__ctas">
              {tx(shopLabel) ? (
                <PortalLink href={shopHref} className="ae-btn ae-btn--accent">{tx(shopLabel)}</PortalLink>
              ) : null}
              {tx(designLabel) ? (
                <PortalLink href={designHref} className="ae-btn ae-hero__quiet">{tx(designLabel)}</PortalLink>
              ) : null}
            </div>
            {shownChips.length ? (
              <nav className="ae-hero__chips" aria-label={t('chrome.departments')}>
                {shownChips.map((chip) => (
                  <PortalLink key={chip.href} href={chip.href}>{tx(chip.label)}</PortalLink>
                ))}
              </nav>
            ) : null}
          </div>
          <FeaturedGlass slug={featuredSlug} tag={featuredTag} />
        </div>
      </div>
      {shownJourneys.length ? (
        <div className="ae-wrap ae-paths">
          {shownJourneys.map((journey) => (
            <PortalLink key={journey.href} href={journey.href} className="ae-path">
              {tx(journey.kicker) ? <span className="ae-path__kicker">{tx(journey.kicker)}</span> : null}
              <h2 className="ae-path__title">{tx(journey.title)}</h2>
              {tx(journey.text) ? <span className="ae-path__text">{tx(journey.text)}</span> : null}
              {tx(journey.label) ? (
                <span className="ae-path__go">
                  {tx(journey.label)} <span aria-hidden="true">→</span>
                </span>
              ) : null}
            </PortalLink>
          ))}
        </div>
      ) : null}
    </BlockRoot>
  );
}

/**
 * The glass spotlight: one real product, read live by slug — title, photograph, MPN, up to
 * three catalogue attributes, the live price on its published basis and the basket action.
 * Nothing here is typed into page data, so nothing can go stale or be invented: a slug that
 * no longer resolves renders no card at all, and while the product loads a quiet placeholder
 * holds the card's space so the hero does not jump.
 */
function FeaturedGlass({ slug, tag }: { slug?: string; tag?: Localized }) {
  const t = useSolarText();
  const trimmed = slug?.trim() ?? '';
  const lines = trimmed ? [{ slug: trimmed }] : undefined;
  const { products, loading, total } = useLiveLines(lines);
  const { available, busy, state, add } = useAddLines(lines, products);
  const product = products[0];

  if (!trimmed || product === null) return null;
  if (!product) return loading ? <article className="ae-glass ae-glass--loading" aria-hidden="true" /> : null;

  const specs = (product.attributes ?? []).filter((attribute) => attribute.value).slice(0, 3);
  const outOfStock = product.availability === 'OUT_OF_STOCK';
  // `heroImage` is the LISTING card's field; the detail payload this block reads
  // carries `images[]` instead, so prefer that and keep heroImage as the fallback.
  const photo = product.images?.find((image) => image.url) ?? product.heroImage;
  return (
    <article className="ae-glass">
      {tx(tag) ? <span className="ae-glass__tag">{tx(tag)}</span> : null}
      <PortalLink href={product.publicPath} className="ae-glass__media">
        {photo?.url ? (
          <img src={photo.url} alt={photo.alt || product.title} />
        ) : (
          <TileGlyph icon="panel" />
        )}
      </PortalLink>
      <h2 className="ae-glass__name">
        <PortalLink href={product.publicPath}>{product.title}</PortalLink>
      </h2>
      {product.mpn ? <span className="ae-glass__mono">{product.mpn}</span> : null}
      {specs.length ? (
        <ul className="ae-glass__specs">
          {specs.map((attribute) => (
            <li key={attribute.code}>{attribute.label}: {attribute.value}</li>
          ))}
        </ul>
      ) : null}
      <div className="ae-glass__buy">
        <LivePrice total={total} loading={loading} />
        {available ? (
          <AddButton
            label={t('home.addToBasket')}
            state={state}
            disabled={!total || busy}
            outOfStock={outOfStock}
            onAdd={add}
            tone="brand"
          />
        ) : null}
      </div>
    </article>
  );
}
